/**
 * Чем и на сколько работали (§51, §35).
 *
 * Отвечает на вопрос «какой сервис и какая модель где использовались».
 * Провайдер — компания, модель — то, что реально сделало кадр, и меняются
 * они порознь: за месяц одна и та же компания может отдать работу трём
 * разным моделям.
 *
 * Дорогое сверху: с него и начинают, когда смотрят, куда уходят деньги.
 * Себестоимость видна только здесь — наружу она не выходит никогда (§35).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../../api/admin';
import { formatNumber } from '../../i18n/format';

const PERIODS = [1, 7, 30, 90];

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const day = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—';

export function AdminUsagePage() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);

  const usage = useQuery({
    queryKey: ['admin', 'usage', days],
    queryFn: () => adminApi.usage(days),
  });

  const rows = usage.data ?? [];
  const total = rows.reduce((sum, row) => sum + row.costCents, 0);
  const calls = rows.reduce((sum, row) => sum + row.calls, 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {PERIODS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              className={
                value === days
                  ? 'rounded-control border border-accent px-3 py-1.5 text-meta text-content'
                  : 'rounded-control border border-line px-3 py-1.5 text-meta text-content-muted transition-colors duration-fast hover:text-content'
              }
            >
              {t('admin.usage.days', { count: value })}
            </button>
          ))}
        </div>

        <p className="text-meta text-content-muted">
          {t('admin.usage.total', { calls: formatNumber(calls), cost: money(total) })}
        </p>
      </div>

      <div className="overflow-x-auto rounded-card border border-line bg-surface">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="border-b border-line text-meta text-content-muted">
              <th className="px-4 py-3 font-normal">{t('admin.usage.provider')}</th>
              <th className="px-4 py-3 font-normal">{t('admin.usage.model')}</th>
              <th className="px-4 py-3 font-normal">{t('admin.usage.kind')}</th>
              <th className="px-4 py-3 text-right font-normal">{t('admin.usage.calls')}</th>
              <th className="px-4 py-3 text-right font-normal">{t('admin.usage.failed')}</th>
              <th className="px-4 py-3 text-right font-normal">{t('admin.usage.cost')}</th>
              <th className="px-4 py-3 text-right font-normal">{t('admin.usage.lastUsed')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.provider}-${row.model ?? 'none'}-${row.kind}`}
                className="border-b border-line last:border-0"
              >
                <td className="px-4 py-3 text-body text-content">{row.provider}</td>
                <td className="px-4 py-3 text-body text-content-secondary">
                  {/* Пусто у вызовов, сделанных до появления этой отметки. */}
                  {row.model ?? t('admin.usage.unknownModel')}
                </td>
                <td className="px-4 py-3 text-meta text-content-muted">{row.kind}</td>
                <td className="px-4 py-3 text-right text-body tabular-nums text-content">
                  {formatNumber(row.calls)}
                </td>
                <td
                  className={
                    row.failed > 0
                      ? 'px-4 py-3 text-right text-body tabular-nums text-error'
                      : 'px-4 py-3 text-right text-body tabular-nums text-content-muted'
                  }
                >
                  {row.failed}
                </td>
                <td className="px-4 py-3 text-right text-body tabular-nums text-content">
                  {money(row.costCents)}
                </td>
                <td className="px-4 py-3 text-right text-meta text-content-muted">
                  {day(row.lastUsedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {rows.length === 0 && !usage.isPending ? (
          <p className="px-4 py-6 text-body text-content-muted">{t('admin.usage.empty')}</p>
        ) : null}
      </div>
    </div>
  );
}
